import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GoogleSignInButton, OrDivider } from "@/components/GoogleSignInButton";
import { PendingAuthStep } from "@/components/AuthSteps";

export default function Register() {
  const { register, setSession } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);

  const onStepDone = (data) => {
    if (data.user) {
      setSession(data);
      toast.success("Email verified — welcome to KMS-Obligo");
      navigate("/");
    } else {
      setPending(data);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const res = await register(name, email, password);
    setLoading(false);
    if (res.pending) {
      setPending(res.pending);
      toast.success("Account created — check your email for the code");
    } else if (res.ok) {
      toast.success("Account created");
      navigate("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid-backdrop grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 bg-white border-r border-slate-200">
        <div className="flex items-center gap-3">
          <img src="/kms-logo.png" alt="KMS logo" className="w-12 h-12 rounded-full object-contain" />
          <div>
            <div className="font-semibold text-lg tracking-tight leading-none">KMS-Obligo</div>
            <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Admin suite for corporates &amp; family offices</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="text-4xl font-semibold tracking-tight leading-[1.05]">
            Less chasing.<br />
            <span className="text-emerald-600">More control.</span>
          </h1>
          <p className="mt-6 text-sm text-slate-600 leading-relaxed">
            Set up your organisation once, invite your team, and KMS-Obligo keeps work, time and
            obligations on track, with reminders before anything slips.
          </p>
        </div>
        <div className="text-xs text-slate-500 mono">Set up in under a minute.</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        {pending ? (
          <PendingAuthStep pending={pending} onDone={onStepDone} onBack={() => navigate("/login")} />
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm bg-white border border-slate-200 rounded-md p-8 animate-fade-up">
          <h2 className="text-2xl font-semibold tracking-tight mb-1" data-testid="register-heading">Create administrator account</h2>
          <p className="text-sm text-slate-500 mb-6">This creates the owner (administrator) account for your organisation. Team members should be invited from the Users page after you sign in.</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-wider text-slate-500">Full name</Label>
              <Input data-testid="register-name" id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input data-testid="register-email" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 mono" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">Password</Label>
              <Input data-testid="register-password" id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 mono" />
              <p className="text-[11px] text-slate-500 mt-1">Minimum 6 characters</p>
            </div>
            <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </div>
          <OrDivider />
          <GoogleSignInButton testid="register-google-btn" label="Sign up with Google" />
          <p className="text-sm text-slate-500 mt-6 text-center">
            Already have an account?{" "}
            <Link data-testid="link-to-login" to="/login" className="font-medium text-zinc-950 underline underline-offset-4">Sign in</Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
